import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Check, Images, Loader2, X } from "lucide-react";
import { api } from "@/lib/api";
import { tr } from "@/lib/i18n";

/**
 * "Add to Stock" for an image that is ALREADY on the article — the
 * featured image (uploaded, imported, picked or AI) and each body
 * image. Saves it to Newsdesk Stock live, right away; no waiting for
 * the article to publish.
 *
 * Before this existed the only route in was the "Add this photo to
 * Newsdesk Stock" checkbox, which had to be ticked BEFORE uploading
 * and then held the photo as pending until publish — ticking it with
 * an image already showing did nothing, so it looked broken.
 *
 * States per image (GET /stock-library/article-status/{id}):
 *   - active  → "In Stock" marker
 *   - pending → "Add to Stock now" (activates the stashed full-size copy)
 *   - none    → "Add to Stock" (saves the article's stored copy)
 *
 *   const artStock = useArticleStock(article);
 *   <ArticleStockButton artStock={artStock} url={article.generated_image_url}
 *     previewSrc={generatedUrl} defaultCredit="…" />
 * `url` is the image's stored URL exactly as on the article (the server
 * checks it); `previewSrc` is what the dialog's <img> shows (defaults
 * to `url`).
 */
export function useArticleStock(article) {
  const status = useQuery({
    queryKey: ["stock-library-status"],
    queryFn: api.stockLibraryStatus,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const configured = !!status.data?.configured;
  const hasImages =
    !!article?.generated_image_url || (article?.body_images || []).length > 0;
  const saved = useQuery({
    queryKey: ["stock-article-status", article?.id],
    queryFn: () => api.stockArticleStatus(article.id),
    enabled: configured && hasImages && !!article?.id,
    staleTime: 60 * 1000,
    retry: false,
  });
  return { article, configured, savedMap: saved.data?.saved || {} };
}

export function ArticleStockButton({
  artStock, url, previewSrc = "", defaultCredit = "", compact = false, className = "",
}) {
  const [open, setOpen] = useState(false);
  if (!artStock?.configured || !url) return null;
  const entry = artStock.savedMap[url];
  if (entry?.status === "active") {
    return (
      <span
        data-testid="article-image-in-stock"
        title={tr("This image is in Newsdesk Stock")}
        className={
          "inline-flex items-center gap-1 text-[var(--ink-muted)] " +
          (compact ? "text-[11px] " : "px-2 py-1 text-xs ") +
          className
        }
      >
        <Check className="w-3.5 h-3.5" />{" "}{tr("In Stock")}</span>
    );
  }
  const pending = entry?.status === "pending";
  return (
    <>
      <button
        type="button"
        data-testid="article-image-add-to-stock"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(true);
        }}
        title={
          pending
            ? tr("Saved earlier as pending — make it live in Newsdesk Stock now")
            : tr("Save this image to Newsdesk Stock now")
        }
        className={
          "inline-flex items-center gap-1.5 border border-[var(--border)] hover:border-[var(--ink)] hover:bg-[var(--secondary)] " +
          (compact ? "px-2 py-0.5 text-[11px] " : "px-3 py-1.5 text-sm ") +
          className
        }
      >
        <Images className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} />
        {pending ? tr("Add to Stock now") : tr("Add to Stock")}
      </button>
      {open && (
        <AddArticleImageDialog
          article={artStock.article}
          url={url}
          previewSrc={previewSrc || url}
          pending={pending}
          defaultCredit={defaultCredit}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AddArticleImageDialog({ article, url, previewSrc, pending, defaultCredit, onClose }) {
  const qc = useQueryClient();
  const [keywords, setKeywords] = useState("");
  const [credit, setCredit] = useState(defaultCredit || "");
  const [title, setTitle] = useState("");

  const save = useMutation({
    mutationFn: () => api.addArticleImageToStock(article.id, url, { keywords, credit, title }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["stock-article-status", article.id] });
      qc.invalidateQueries({ queryKey: ["stock-library"] });
      toast.success(
        res?.duplicate ? tr("This photo is already in Newsdesk Stock") : tr("Added to Newsdesk Stock"),
      );
      onClose();
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || e?.message || tr("Could not add to Newsdesk Stock")),
  });

  // Rendered inside clickable areas: stop every click here so it never
  // reaches the image panel underneath.
  const stop = (e) => e.stopPropagation();

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center px-4"
      onClick={(e) => {
        stop(e);
        if (!save.isPending) onClose();
      }}
    >
      <div
        onClick={stop}
        data-testid="article-stock-dialog"
        className="w-full max-w-md bg-white border border-[var(--border)] shadow-xl text-left"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--border)]">
          <div>
            <div className="overline">{tr("Newsdesk Stock")}</div>
            <h2 className="font-serif-display text-lg">{tr("Add this image")}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--ink-muted)] hover:text-[var(--ink)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          <div className="aspect-video bg-[var(--secondary)] border border-[var(--border)] overflow-hidden">
            <img src={previewSrc} alt="" className="w-full h-full object-contain" />
          </div>
          <input
            data-testid="article-stock-keywords"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder={
              pending
                ? tr("Keywords (leave blank to keep the ones saved earlier)")
                : tr("Keywords, comma separated (e.g. malecón, ensenada, sunset)")
            }
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <input
            data-testid="article-stock-credit"
            value={credit}
            onChange={(e) => setCredit(e.target.value)}
            placeholder={tr("Preset caption / credit (e.g. Photo: Oliver Quintero)")}
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <input
            data-testid="article-stock-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={tr("Title (optional)")}
            className="w-full text-sm px-2 py-1.5 border border-[var(--border)] bg-white focus:border-[var(--ink)] outline-none"
          />
          <div className="text-[11px] text-[var(--ink-muted)]">
            {pending
              ? tr("The full-size original saved earlier goes live now.")
              : tr("Saved as it is stored on the article (up to 1200 px wide), without the disclosure legend.")}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-[var(--border)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={save.isPending}
            className="px-3 py-1.5 text-sm border border-[var(--border)] hover:border-[var(--ink)] disabled:opacity-60"
          >{tr("Cancel")}</button>
          <button
            type="button"
            data-testid="article-stock-save"
            onClick={() => save.mutate()}
            disabled={save.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-[var(--ink)] text-white hover:opacity-90 disabled:opacity-60"
          >
            {save.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{tr("Add to Stock")}</button>
        </div>
      </div>
    </div>
  );
}
